import { useEffect, useRef, useCallback, useState, forwardRef, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import TenantPicker from '@components/tenant/TenantPicker.component';
import AuthService, { useAuthState } from "../../services/auth/auth.service";
import logoLarge from '../../assets/images/pliable/logo_with_text_black.png';
import { PliableIcon } from '@components/icons/PliableIcons.component';
import { AuthorizedContent, AdminPortal } from "@frontegg/react";
import { Role } from "../../constants/roles";
import TrackingService, {Events} from '@services/tracking.service';
import styled from 'styled-components';
import { useHotkeys } from 'react-hotkeys-hook';
import { pickTemplate } from '@services/alert/alert.service';
import { toggleThenaWidget, useIsInDraftMode, usePipelineNode, useProjectConfig } from "@stores/data.store";
import ApiService from '@services/api/api.service';
import { enterDraftMode, exitDraftMode, useDraftVersionId } from '@stores/data.store';
import { ProdOnly } from '@components/project/DraftModeRequired.component';
import { useFeatureFlagValue } from '@services/featureFlags/featureFlag.service';
import { Badge, Form } from 'react-bootstrap';
import { getGroupValueForNodeType } from '@services/modeling.service';
import { confirmRunBuild } from '@components/Inbox.component';

const Styles = styled.div`
    z-index: 10;
    border-right: solid 1px var(--ct-border-color);


    &.sidebar-compact {
        width: 60px;
        .link-label {
            display: none;
        }

        .nav-item ul.nav {
            display: none;
        }

        .nav-item button {
            text-align: center !important;
        }

        .nav-link {
            text-align: center;
        }
    }

    span.link-label {
        margin-left: .5rem;
    }

    padding-right: 1rem;

    ul.nav {
        opacity: 1;
        padding-left: 1rem;

    }

    text-align: left;
    width: 200px;
    background-color: var(--ct-body-bg);
    height: 100vh;

    &.sidebar-draft {
        height: calc(100vh - 50px);
    }

    .nav.reverse {
        .nav-item {
            button, .fake-button {
                padding: .5rem 5px;
                font-weight: 600;
                text-align: left;
                width: 100%;
                border: none;
                background-color: inherit;
                display: flex;
                flex-direction: row;

                .link-label {
                    flex: 1;
                }
                border-radius: .25rem;

                &:hover {
                    background-color: var(--brand-gray-3);
                }
            }

            &.special {
                border: solid 1px var(--brand-blue);
                border-radius: .25rem;
            }
            

        }

        

        &.noclick button {
            &:hover {
                background-color: inherit !important;
            }
        }
    }

    .nav-item {
        font-size: 1em;
        font-family: "Onest";
        width: 100%;
        font-weight: 600;

        &.tenant-picker {
            border: solid 1px var(--ct-border-color);
            padding: .5rem;
            border-radius: 5px;
            margin-bottom: 20px;
            font-size: 13px;
            font-weight: 300;
            background: white;
        }

        .nav-item .nav-link {
            padding: .25rem 5px !important;
            font-weight: 300;
        }
        
        button.bottom {
            border-radius: 100%;
            height: 40px;
            width: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0px;
            text-align: center;
            color: white;
            font-size: 24px;
            margin: 0px auto;
        }
        .nav-link, .nav-link-fake {
            padding: .5rem 5px;
        }
        .nav-link {
            color: var(--ct-body-color);
            &.active {
                background-color: var(--brand-blue);
                color: white;
                
            }
            &:not(.noclick):not(.active):hover {
                background-color: var(--brand-gray-3);
                text-decoration: none;
            }

            &:hover {
                text-decoration: none;
            }
        }
    }

    .version {
        font-size: .8em;
        color: #666;
    }

    .user-badge{
        line-height: 1;
        img {
            margin-right: 5px;
        }

        .actor-label{
            padding-top: 5px;
        }

        .logout-link {
            font-size: .8em;
        }
    }
`;

// @ts-ignore
const AuthToggle = forwardRef(({ children, onClick, disabled }, ref) => (
    <button
    //   @ts-ignore
      ref={ref}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
      style={{
        padding: '0px',
        background: 'none',
        border: 'none',
      }}
    >
      {children}
    </button>
  ));


function useComponentVisible(initialIsVisible: boolean) {
    const [isComponentVisible, setIsComponentVisible] = useState(initialIsVisible);
    const ref = useRef<HTMLLIElement>(null);
    

    const handleClickOutside = (event: any) => {
        // console.log('Click outside:', event);
        if (ref.current && !ref.current.contains(event.target)) {
            setIsComponentVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    return { ref, isComponentVisible, setIsComponentVisible };
}

export interface NavParams {
    compact?: boolean;
    inDraftMode?: boolean;
}

const AppSidebar = (params: NavParams = {}) => {
    const navigate = useNavigate();
    const {data: draftVersionId} = useDraftVersionId();
    const {dataUpdatedAt: tcUpdatedAt, data: tenantConfig} = useProjectConfig();
    const logout = () => {
        navigate('/logout');
    }

    const settings = () => {
        navigate('/account');
    }

    useHotkeys('ctrl+t', () => {
        pickTemplate({
            onTemplateSelected: (tmpl_id) => { navigate(`/template/${tmpl_id}?autouse=true`) }
        })
    }, []);

    useHotkeys('ctrl+a', () => {
        navigate(`/advanced/new`)
    }, []);

    useHotkeys('ctrl+y', () => {
        navigate(`/templates/admin`)
    }, []);

    useHotkeys('ctrl+d', () => {
        onToggleDraftMode('hotkey');
    }, [draftVersionId]);

    useHotkeys('ctrl+y', () => {
        navigate(`/templates/admin`)
    }, []);

    // If we're looking at a node, highlight the sidebar link based on what type of node it is.
    const location = useLocation();
    const inNode = location.pathname.indexOf('/node/') >= 0;
    const { pipelineNodeId } = useParams();

    const currentNode = usePipelineNode(inNode ? pipelineNodeId as string : '');

    const highlightNodeType = useMemo(() => {
        if (!inNode) {
            return '';
        }

        const group = getGroupValueForNodeType(currentNode.data?.node_type as string);
        if (group && group == 'STAGING') {
            return 'cleaning';
        } else if (group && group == 'DATA_MODELING') {
            return 'entities';
        } else if (currentNode.data?.node_type == 'DESTINATION') {
            return 'exports';
        } else if (currentNode.data?.node_type == 'REPORT') {
            return 'exports';
        }
        
        return '';
    }, [currentNode.dataUpdatedAt, inNode]);

    const [animating, setAnimating] = useState(false);
    useEffect(() => {
        setAnimating(true);
        setTimeout(() => {
            setAnimating(false);
        }, 250);
    }, [params.compact])

    const inDraftMode = useIsInDraftMode();
    


    const authState = useAuthState();

    // const { sidebarExpanded, toggleSidebar} = useGlobalState((state: any) => state);

    const sidebarExpanded = !params.compact;

    let extraClass = '';

    if (!sidebarExpanded) {
        extraClass = 'sidebar-compact';
    }


    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    const username = AuthService.getInstance().plbAuthState?.email;
    const tenant_name = AuthService.getInstance().getTenantName();

    const onToggleDraftMode = useCallback(async (trigger: string) => {
        if(draftVersionId) {
            await exitDraftMode(trigger);
        }else{
            await enterDraftMode(trigger);
        }
    }, [draftVersionId]);

    const showAdminPortal = useCallback(() => {
        AdminPortal.show();
        TrackingService.track_event(Events.USER_ADMIN_CLK);
    }, []);

    const showAssistant = useCallback(() => {
        navigate('/data-assistant');
    }, []);

    const trackLogout = useCallback(() => {
        TrackingService.track_event(Events.USER_LOGOUT_CLICK);
    }, []);

    const runSchedulerCheck = useCallback(async () => {
        const service = ApiService.getInstance();
        const response = await fetch(service.hostname + '/system/scheduler/check', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + AuthService.getInstance().getAccessToken(),
            },
            mode: 'cors',
            credentials: 'include',
        });
    }, []);

    const hostedSupersetEnabled = useFeatureFlagValue('hosted-superset', false);
    const pliableVisualizationsEnabled = useFeatureFlagValue('pliable-visualizations', false);
    const aiAssistantEnabled = useFeatureFlagValue('ai-assistant', false);
    const contextEnabled = useFeatureFlagValue('context', false);


    const logo = useMemo(() => {
        if (!tenantConfig) {
            return;
        }

        if (tenantConfig && tenantConfig.whitelabeling && tenantConfig.whitelabeling.logo_path) {
            return tenantConfig.whitelabeling.logo_path;
        }
        return logoLarge;
    }, [tcUpdatedAt, tenantConfig]);

    return (
    <Styles className={`d-flex flex-column flex-shrink-0  ${extraClass} ${animating && 'transparent'} ${params.inDraftMode && 'sidebar-draft'}`}>
        <ul className="nav nav-pills nav-flush flex-column text-start">
            <li className="nav-item  text-start" >
                
                <Link className={`nav-link noclick py-3`} to="/" title="Dashboard">
                    {!!sidebarExpanded && (
                        <img src={logo} style={{height: '27px'}}/>
                    )}
                    {!sidebarExpanded && (
                        <PliableIcon height='50px' />
                    )}
                </Link>
            </li>
            <li className="nav-item tenant-picker" data-testid="tenant-picker">
                <TenantPicker compact={params.compact}/>
            </li>
        </ul>
        <ul className="nav nav-pills  mb-auto">
            <li className="nav-item">
                <Link className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} to="/" title="Dashboard">
                    <i className="mdi mdi-home"></i>
                    <span className="link-label">Home</span>
                </Link>
            </li>
            <li className="nav-item">
                <Link className={`nav-link ${location.pathname.startsWith('/sources') ? 'active' : ''}`} to="/sources" title="Sources">
                    <i className="mdi mdi-database-import"></i>
                    <span className="link-label">Data Sources</span>
                </Link>
            </li>
            <li className="nav-item">
                <span className="nav-link noclick">
                    <i className="mdi mdi-archive"></i>
                    <span className="link-label">Organization</span>
                </span>
                <ul className="nav">
                    <li className="nav-item">
                        <Link className={`nav-link ${highlightNodeType == 'cleaning' ||location.pathname.startsWith('/cleaning') ? 'active' : ''}`} to="/cleaning" title="Cleaning">
                            <span className="link-label">Cleaning</span>
                        </Link>

                    </li>
                    <li className="nav-item">
                        <Link className={`nav-link ${location.pathname.startsWith('/model') || location.pathname.startsWith('/metrics') || highlightNodeType == 'entities' ? 'active' : ''}`} to="/model/list" title="Data Modeling">
                            <span className="link-label">Semantic Layer</span>
                        </Link>

                    </li>
                    {contextEnabled && <li className="nav-item">
                        <Link className={`nav-link ${location.pathname.startsWith('/context') ? 'active' : ''}`} to="/context" title="Context">
                            <span className="link-label">Context</span>
                        </Link>

                    </li>}
                    <li className="nav-item">
                        <Link className={`nav-link ${highlightNodeType == 'exports' || location.pathname.startsWith('/exports') ? 'active' : ''}`} to="/exports" title="Exports">
                            <span className="link-label">Exports</span>
                        </Link>
 
                    </li>
                </ul>
            </li>
            
            <li className="nav-item">
                <span className="nav-link noclick">
                    <i className="mdi mdi-chart-box"></i>
                    <span className="link-label">Insights</span> 
                </span>
                <ul className="nav">
                    <li className="nav-item">
                        <Link className={`nav-link ${location.pathname.startsWith('/dashboards') ? 'active' : ''}`} to="/dashboards" title="Dashboards">
                            <span className="link-label">Dashboards</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className={`nav-link ${location.pathname.startsWith('/charts') ? 'active' : ''}`} to="/charts" title="Charts">
                            <span className="link-label">Charts</span>
                        </Link>
                    </li>
                    {aiAssistantEnabled && <li className="nav-item">
                        <Link className={`nav-link ${location.pathname.startsWith('/ellie') ? 'active' : ''}`} to="/ellie" title="Ask Ellie">
                            <span className="link-label">Ask Ellie</span>
                        </Link>
                    </li>}
                    {!aiAssistantEnabled && <li className="nav-item">
                        <a className={`nav-link noclick`} title="AI Assistant">
                            <span className="link-label">Ask AI (Coming Soon)</span>
                        </a>
                    </li>}
                    
                    
                    
                    
                    {hostedSupersetEnabled && <ProdOnly><li className="nav-item">
                        <Link to="/bi" className={`nav-link ${location.pathname.startsWith('/bi') ? 'active' : ''}`}>
                            <span className="link-label">Superset</span>
                        </Link>
                    </li></ProdOnly>}
                </ul>
            </li>
            
            <li className="nav-item">
                <a onClick={showAdminPortal} className="nav-link" title="User Admin">
                    <i className="mdi mdi-account-group"></i>
                    <span className="link-label">Manage Users</span>
                </a>
            </li>
            <AuthorizedContent requiredRoles={[Role.Admin]} key="tenant-admin-wrap" >
            <li className="nav-item">
                <Link className={`nav-link ${location.pathname === '/settings' ? 'active' : ''}`} to="/settings" title="Account Settings">
                    <i className="mdi mdi-cog"></i>
                    <span className="link-label">Account Settings</span>
                </Link>
            </li>
            </AuthorizedContent>
            
        </ul>
        <ul className="nav reverse">
            <li className="nav-item">
                <button onClick={toggleThenaWidget}>
                    <div className="link-label">Get Help</div>
                    <div className="icon"><i className="mdi mdi-chat"></i>
                    </div>

                </button>
            </li>
            <li className="nav-item">
                <button onClick={() => confirmRunBuild()}>
                    <div className="link-label">Refresh All</div>
                    <div className="icon"><i className="mdi mdi-refresh"></i>
                    </div>

                </button>
            </li>
            {/* <li className="nav-item noclick">
                <button>
                    <div className="link-label">Status</div>
                    {params.compact && <>
                        <i className="mdi mdi-check-circle text-success"></i>
                    </>}
                    {!params.compact && <>
                        <Badge bg="success">No issues</Badge>

                    </>}

                </button>
            </li> */}
            <li className="nav-item">
                {/* <button> */}
                <div className="d-flex fake-button pointer" onClick={() => {
                    onToggleDraftMode('sidebar');
                }}>
                    <div className="link-label flex-1">Develop Mode</div>
                    <Form.Check
                        type="switch"
                        checked={inDraftMode}
                        
                    />
                </div>
                    

                {/* </button> */}
            </li>
        </ul>
        <div className="flex-column p-2 mt-2 border-top">

            <div title={authState?.email} className='d-flex flex-row user-badge'>
                <div><img src={authState?.profile_img_url as string | undefined} alt="mdo" width="40" height="40" className="rounded-circle" /></div>
                <div className="actor-label">
                    <div className='overflow-ellipsis'>{authState?.name}</div>
                    <Link to="/logout" className="logout-link">Logout</Link>
                </div>
                
            </div>
        </div>

        

    </Styles>
    );

}


export default AppSidebar;